import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { xor, isEqual } from "../../utils/lodash";
import useStyles from "./Select.styles";
import { RecalledText } from "../../containers/shared/RecalledText";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import useRecall from "../../hooks/useRecall";
import { removeHtmlTags } from "../../utils";
import InputWrapper from "../shared/InputWrapper";
import useQuestion from "../../hooks/useQuestion";
import { QuestionType } from "../../models/enums/QuestionType";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { DefaultNavigation } from "../controls/KeyboardNavigation/DefaultNavigation";
import { VariableModel } from "../../models/VariableModel";

export interface Option {
    id: string;
    label: string;
    value: string;
    isNonInteractive?: boolean;
}

interface Props {
    id: string;
    labelId?: string;
    className?: string;
    required?: boolean;
    multiple?: boolean;
    value: string[];
    options: Option[];
    onChange(value: string[]): void;
    icon?: IconProp;
    disabled?: boolean;
    size?: string;
    listboxAriaLabelId?: string;
    ariaDisabledBy?: boolean;
    variable?: VariableModel;
}

interface OptionProps {
    option: Option;
    typeAhead: string;
    multiple?: boolean;
    selected?: boolean;
    elementReference: React.RefObject<HTMLDivElement>;
    onSelectOption(option: Option);
    onDetectCharacter(key: string);
    onKeyDown(e: React.KeyboardEvent);
    onCloseListBox();
    onSelectPreviousOption();
    onSelectNextOption();
    onSelectFirstOption();
    onSelectLastOption();
}

enum SelectionDirection {
    Previous,
    Next,
}

function ListBoxOption(props: OptionProps) {
    const {
        option,
        typeAhead,
        multiple,
        selected,
        onDetectCharacter,
        onSelectOption,
        onSelectPreviousOption,
        onKeyDown,
        onSelectNextOption,
        onSelectFirstOption,
        onSelectLastOption,
        onCloseListBox,
        elementReference,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme });

    function onOptionKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter" || (e.key === " " && !typeAhead.length)) {
            if (option.isNonInteractive) {
                e.preventDefault();
                return;
            }
            onSelectOption(option);
            if (!multiple) {
                onCloseListBox();
                elementReference.current.focus();
            }
            e.preventDefault();
            return;
        }
        if (e.key === "Escape") {
            elementReference.current.focus();
            onCloseListBox();
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowUp") {
            onSelectPreviousOption();
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowDown") {
            onSelectNextOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Home") {
            onSelectFirstOption();
            e.preventDefault();
            return;
        }
        if (e.key === "End") {
            onSelectLastOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Tab") {
            onKeyDown(e);
            e.preventDefault();
        }
        if (onDetectCharacter(e.key)) {
            e.preventDefault();
        }
    }

    function onClickOption(e: React.MouseEvent) {
        if (option.isNonInteractive) {
            e.preventDefault();
            return;
        }
        onSelectOption(option);
        if (!multiple) {
            onCloseListBox();
        }
        e.preventDefault();
    }

    return (
        <li
            id={option.id}
            key={option.value}
            role="option"
            onClick={onClickOption}
            onKeyDown={onOptionKeyDown}
            className={option.isNonInteractive ? classes.optionNonInteractive : classes.option}
            tabIndex={-1}
            aria-label={removeHtmlTags(option.label)}
            aria-selected={option.isNonInteractive ? undefined : selected}
        >
            <RecalledText text={option.label} ariaHidden={true} />
            <div className="flex" />
            {multiple && (
                <FontAwesomeIcon
                    icon={["far", "check"]}
                    className={classnames(classes.check, !selected && classes.invisible)}
                />
            )}
        </li>
    );
}

export default function Select(props: Props) {
    const {
        options,
        multiple,
        required,
        id,
        labelId,
        className,
        value,
        onChange,
        size,
        icon,
        disabled,
        listboxAriaLabelId,
        ariaDisabledBy,
        variable,
    } = props;
    const question = useQuestion();
    const isHybridGrid =
        question && question?.parent && question?.parent?.type === QuestionType.HybridGrid;

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        DefaultNavigation,
        null,
    );
    const optionsRef = React.useRef(options);
    const valueRef = React.useRef(value);
    const selectedOptions = React.useMemo(() => {
        const selected = options.filter((o) => o.value && value.includes(o.value));
        if (!selected.length) {
            return [options[0]];
        }
        return selected;
    }, [options, value]);

    const displayedOptions = React.useMemo(() => {
        if (multiple) {
            return options.filter((option) => option.value !== "");
        }
        return options;
    }, [options, multiple]);

    const defaultFocusedOption = displayedOptions.includes(selectedOptions[0])
        ? selectedOptions[0]
        : displayedOptions[0];

    const ref = React.useRef<HTMLDivElement>();

    const theme = useTheme();
    const classes = useStyles({ theme, icon, size, disabled });

    const [dropdownWidth, setDropdownWidth] = React.useState(0);
    const [focused, setFocused] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const [typeAhead, setTypeAhead] = React.useState("");
    const [focusedOption, setFocusedOption] = React.useState(defaultFocusedOption);
    const [active, setActive] = React.useState(false);
    const ariaAttributes = {};

    if (listboxAriaLabelId) {
        ariaAttributes["aria-labelledby"] = listboxAriaLabelId;
    }

    if (ariaDisabledBy) {
        ariaAttributes["aria-disabled"] = ariaDisabledBy;
    }

    React.useEffect(() => {
        valueRef.current = value;
    }, [value]);

    React.useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    React.useLayoutEffect(() => {
        if (!expanded) {
            return undefined;
        }
        function measure() {
            const rect = ref.current.getBoundingClientRect();
            if (!isEqual(dropdownWidth, rect.width)) {
                setDropdownWidth(rect.width);
            }
        }
        measure();
    }, [expanded]);

    React.useEffect(() => {
        // Set the focused option to the selected option when the control is not multi-selectable.
        if (!multiple) {
            setFocusedOption(selectedOptions[0]);
        }
    }, [multiple, selectedOptions]);

    React.useLayoutEffect(() => {
        // When focusedOption changes value, bring it in focus.
        if (expanded && focusedOption) {
            const el = document.getElementById(focusedOption.id);
            if (el) {
                el.focus();
            }
        }
    }, [expanded, focusedOption]);

    function focusOption(option) {
        setFocusedOption(option);
    }

    const selectOption = React.useCallback(
        (option) => {
            // If the select component is inside a Hybrid Grid, adapt the scrollable container style
            if (ref && ref.current) {
                toggleContainerStyle(!expanded);
            }
            if (multiple) {
                onChange(xor(valueRef.current, [option.value]));
            } else {
                onChange([option.value]);
            }
        },
        [expanded, multiple, onChange],
    );

    React.useEffect(() => {
        let timeout;
        if (typeAhead.length) {
            timeout = setTimeout(function resetTypeAhead() {
                setTypeAhead("");
            }, 500);
            const nextMatchedOption = optionsRef.current.find((o) =>
                o.label.toLowerCase().startsWith(typeAhead.toLowerCase()),
            );
            if (nextMatchedOption) {
                setFocusedOption(nextMatchedOption);
            }
        }
        return function cleanup() {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        };
    }, [typeAhead, setFocusedOption]);

    React.useEffect(() => {
        function clickOutside(event) {
            if (ref.current.contains(event.target)) {
                return;
            }
            setExpanded(false);
        }
        document.addEventListener("mousedown", clickOutside);
        return function cleanup() {
            document.removeEventListener("mousedown", clickOutside);
        };
    }, []);

    function toggleContainerStyle(showDropDown: boolean) {
        const scrollableWindow = ref.current.closest(".scrollable__window");
        const scrollbarsDiv = ref.current.closest(".scrollbars");

        if (scrollableWindow && scrollbarsDiv) {
            const scrollableWindowElement = scrollableWindow as HTMLElement;
            const scrollbarsDivElement = scrollbarsDiv as HTMLElement;

            if (showDropDown) {
                scrollableWindowElement.style.setProperty("overflow", "visible", "important");
                scrollbarsDivElement.style.setProperty("overflow", "visible", "important");
                (scrollbarsDiv.lastChild as HTMLElement)?.style.setProperty("z-index", "-99");
            } else {
                scrollableWindowElement.style.setProperty("overflow", "auto", "important");
                scrollbarsDivElement.style.setProperty("overflow", "auto", "important");
                (scrollbarsDiv.lastChild as HTMLElement)?.style.removeProperty("z-index");
            }
        }
    }

    function getAdjacentSelectableOption(option: Option, direction: SelectionDirection) {
        const focusedIndex = displayedOptions.indexOf(option);
        const isPrevious = direction === SelectionDirection.Previous;

        const adjacentIndices = Array.from(
            Array(Math.abs(focusedIndex - (isPrevious ? 0 : displayedOptions.length - 1))).keys(),
        ).map((index) =>
            isPrevious ? Math.abs(index + 1 - focusedIndex) : index + focusedIndex + 1,
        );

        const adjacent = adjacentIndices.find((index) => !displayedOptions[index].isNonInteractive);
        return adjacent !== undefined ? displayedOptions[adjacent] : option;
    }

    function selectFirstOption() {
        const [firstOption] = displayedOptions;
        selectOption(
            firstOption.isNonInteractive
                ? getAdjacentSelectableOption(firstOption, SelectionDirection.Next)
                : firstOption,
        );
    }

    function selectLastOption() {
        const lastOption = displayedOptions[displayedOptions.length - 1];
        selectOption(
            lastOption.isNonInteractive
                ? getAdjacentSelectableOption(lastOption, SelectionDirection.Previous)
                : lastOption,
        );
    }

    function selectPreviousOption() {
        const previous = getAdjacentSelectableOption(focusedOption, SelectionDirection.Previous);
        if (multiple) {
            focusOption(previous);
        } else {
            selectOption(previous);
        }
    }

    function selectNextOption() {
        const next = getAdjacentSelectableOption(focusedOption, SelectionDirection.Next);
        if (multiple) {
            focusOption(next);
        } else {
            selectOption(next);
        }
    }

    function closeListBox() {
        setExpanded(false);
        setFocusedOption(defaultFocusedOption);
    }

    function openListBox() {
        setExpanded(true);
    }

    function detectCharacter(key: string) {
        if (/^[\w\d\s]$/i.test(key)) {
            setTypeAhead(typeAhead + key);
            return true;
        }
        return false;
    }

    function onButtonKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter" || (e.key === " " && !typeAhead.length)) {
            if (expanded) {
                closeListBox();
            } else {
                openListBox();
            }
            e.preventDefault();
            return;
        }
        if (!isHybridGrid || (isHybridGrid && expanded)) {
            if (e.key === "ArrowUp") {
                openListBox();
                selectPreviousOption();
                e.preventDefault();
                return;
            }
            if (e.key === "ArrowDown") {
                openListBox();
                selectNextOption();
                e.preventDefault();
                return;
            }
        }

        if (detectCharacter(e.key)) {
            e.preventDefault();
        }
    }

    function handleKeyDown(event) {
        onButtonKeyDown(event);
        onKeyDown(event);
    }

    function onClickSelect(e: React.MouseEvent) {
        if (disabled !== true) {
            // If the select component is inside a Hybrid Grid, adapt the scrollable container style
            if (ref && ref.current) {
                toggleContainerStyle(!expanded);
            }
            if (expanded) {
                closeListBox();
            } else {
                openListBox();
            }
            e.preventDefault();
        }
    }

    const labelsIds = [labelId];
    if (selectedOptions) {
        labelsIds.push(...selectedOptions.map((option) => option.id));
    }

    const parentQuestionType = question?.parent?.type ?? question?.type;

    const hideQuestionInLabel =
        question &&
        (question.type === QuestionType.DropDownList ||
            question.type === QuestionType.RespondentTimeZone ||
            question.type === QuestionType.RespondentLanguage);

    let ariaLabelledForDivIds: any;

    if (hideQuestionInLabel) {
        let idToRemove = question.elementId;

        if (parentQuestionType === QuestionType.HybridGrid) {
            idToRemove = question.parent.elementId;
        }

        ariaLabelledForDivIds = labelsIds.filter((x) => x !== `${idToRemove}_LABEL`).join(" ");
    } else {
        ariaLabelledForDivIds = labelsIds.join(" ");
    }

    const listBoxChildren = displayedOptions.map((option) => (
        <ListBoxOption
            key={option.id}
            option={option}
            typeAhead={typeAhead}
            multiple={multiple}
            selected={selectedOptions.includes(option)}
            elementReference={elementReference}
            onSelectOption={selectOption}
            onDetectCharacter={detectCharacter}
            onKeyDown={onKeyDown}
            onCloseListBox={closeListBox}
            onSelectPreviousOption={selectPreviousOption}
            onSelectNextOption={selectNextOption}
            onSelectFirstOption={selectFirstOption}
            onSelectLastOption={selectLastOption}
        />
    ));

    const recalledLabel = useRecall(selectedOptions[0] && selectedOptions[0].label);

    return (
        <InputWrapper size={size}>
            <div
                ref={ref}
                className={classnames(
                    className,
                    classes.select,
                    focused && classes.selectFocused,
                    expanded && classes.selectExpanded,
                    active && classes.choiceActive,
                    disabled && classes.selectDisabled,
                )}
                id={id}
                onFocus={() => setActive(true)}
                onBlur={(e) => {
                    setActive(false);
                    const target = e.relatedTarget || document.activeElement;
                    // If the element that *gained* focus is not contained within, close the list box.
                    if (!ref.current.contains(target as Node)) {
                        closeListBox();
                    }

                    if (ref && ref.current) {
                        toggleContainerStyle(expanded);
                    }
                }}
            >
                {icon && (
                    <FontAwesomeIcon
                        icon={icon}
                        className={classnames(
                            classes.icon,
                            classes.iconLeft,
                            active && classes.choiceActive,
                        )}
                        onFocus={() => setActive(true)}
                    />
                )}
                <div
                    tabIndex={0}
                    role="button"
                    ref={elementReference}
                    aria-labelledby={ariaLabelledForDivIds}
                    aria-disabled={ariaDisabledBy}
                    className={classes.selectContent}
                    onClick={onClickSelect}
                    onKeyDown={handleKeyDown}
                    onBlur={() => disabled !== true && setFocused(false)}
                    onFocus={() => disabled !== true && setFocused(true)}
                    aria-haspopup="listbox"
                    aria-expanded={expanded}
                    dangerouslySetInnerHTML={{ __html: recalledLabel }}
                />
                {disabled !== true && (
                    <FontAwesomeIcon
                        icon={expanded ? "caret-up" : "caret-down"}
                        className={classnames(classes.icon, classes.iconCaret)}
                    />
                )}
                {/* eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex */}
                <ul
                    role="listbox"
                    className={classnames(
                        classes.listbox,
                        classes.dropDown,
                        !expanded && classes.hidden,
                        expanded && classes.listboxExpanded,
                    )}
                    style={{ minWidth: dropdownWidth }}
                    tabIndex={-1}
                    aria-required={required}
                    aria-activedescendant={selectedOptions[0] && selectedOptions[0].id}
                    {...ariaAttributes}
                >
                    {listBoxChildren}
                </ul>
            </div>
        </InputWrapper>
    );
}
